import { Fragment } from 'react'
import { Trans, useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import ContentWrapper from '~/tutor-on-demand/components/Content/ContentWrapper'
import HeaderTitle from '~/tutor-on-demand/components/Header/HeaderTitle'
import HeaderWrapper from '~/tutor-on-demand/components/Header/HeaderWrapper'
import TutorOnDemandProductCards from '~/shared/components/TutorOnDemandProductCards/TutorOnDemandProductCards.component'
import Paragraph from '~/tutor-on-demand/components/Typography/Paragraph'

import SubContainer from '~/tutor-on-demand/components/Content/SubContainer'
import SubContainerWrapper from '~/tutor-on-demand/components/Content/SubContainerWrapper'
import { SubjectType, getSubjectImage } from '~/components/Metadata/SubjectMetatags'

import ROUTES from '~/shared/constants/routes.const'
import BreadCrumbs from '~/encyclopedia/components/BreadCrumbs'
import { Link } from '~/components'

import { AppLanguage } from '~/types/locale'

import { statisticsTopicList } from '../constants/TutorOnDemandStatisticsList.constants'
import { statisticsTopic } from '../types/tutor-on-demand-statistics.types'

export default function TutorOnDemandStatistics({ type }: { type: SubjectType }): React.ReactElement {
  const { t } = useTranslation(['tutorOnDemand', 'tutorOnDemandStatistics'])
  const breadcrumbs = [
    { path: ROUTES.TUTOR_ON_DEMAND_INFORMATION, h1: t('tutorOnDemand:landingPage.whatIsTutorOnDemand.title') },
    { path: ROUTES.TUTOR_ON_DEMAND_STATISTICS, h1: t('tutorOnDemandStatistics:title') },
  ]
  const locale = useRouter().locale as AppLanguage

  return (
    <Fragment>
      <HeaderWrapper>
        <BreadCrumbs
          breadcrumbs={breadcrumbs}
          fontSize="medium"
          isWhite
          itemClassNames="font-medium items-center"
          wrapperClassNames="mt-1 pr-12 lg:pr-0"
        />

        <HeaderTitle
          iconAlt={getSubjectImage(type, t).alt as string}
          iconSrc={getSubjectImage(type, t).srcKey as string}
        >
          {t('tutorOnDemandStatistics:title')}
        </HeaderTitle>
      </HeaderWrapper>
      <ContentWrapper className="space-y-8">
        <div className="xl:space-x-8 xl:flex">
          <section className="mb-6 space-y-6 xl:mb-0">
            <Paragraph>{t('tutorOnDemandStatistics:contentParagraph1')}</Paragraph>
            <Paragraph>{t('tutorOnDemandStatistics:contentParagraph2')}</Paragraph>
            <Paragraph>{t('tutorOnDemandStatistics:contentParagraph3')}</Paragraph>
            <Paragraph>
              {Trans({
                t,
                i18nKey: 'tutorOnDemandStatistics:contentParagraphEncyclopediaHref',
                components: { a: <a className="underline font-semibold" href={ROUTES.ENCYCLOPEDIA} /> },
                children: t('tutorOnDemandStatistics:contentParagraphEncyclopediaHref'),
              })}
            </Paragraph>
          </section>
          <SubContainerWrapper>
            <SubContainer className="w-full max-w-sm" header={t('tutorOnDemandStatistics:weCanHelpYouWith.header')}>
              <ul className="ml-6 list-disc list-outside text-black-light">
                {Object.values(statisticsTopic).map((value) => (
                  <li key={value}>
                    {!statisticsTopicList[value][locale] ? (
                      t(`tutorOnDemandStatistics:weCanHelpYouWith.items.${value}`)
                    ) : (
                      <Link className="underline font-medium text-purple" to={statisticsTopicList[value][locale]}>
                        {t(`tutorOnDemandStatistics:weCanHelpYouWith.items.${value}`)}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </SubContainer>
          </SubContainerWrapper>
        </div>
        <TutorOnDemandProductCards
          className="pt-8 max-w-max space-y-14 lg:space-y-0 lg:space-x-4 xl:space-x-8 flex flex-col lg:flex-row"
          colorScheme="white"
          hideChatWithTutor
          hideVideoChatWithTutor
        />
      </ContentWrapper>
    </Fragment>
  )
}
